import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, createTheme, type Theme } from '@mui/material';
import { enUS, frFR } from '@mui/material/locale';

/**
 * Puts MUI's own strings in the interface language.
 *
 * MUI writes some text itself — the Autocomplete's "Open" / "Clear" / "No
 * options", pagination's "Rows per page" and "1–10 of 20", the alert's "Close".
 * With no locale on the theme those stayed English in the French interface, as
 * tooltips and screen-reader labels (the front desk's file picker announced
 * "Open" to a francophone clerk).
 *
 * Mounted by each app *inside* its `I18nextProvider` — `AppThemeProvider` sits
 * outside it, where a language switch would never reach — and layered over the
 * app's theme rather than replacing it, so the portal accent is untouched.
 */
export function MuiLocaleProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const fr = (i18n?.language ?? '').toLowerCase().startsWith('fr');
  const localize = useCallback((outer: Theme) => createTheme(outer, fr ? frFR : enUS), [fr]);
  return <ThemeProvider theme={localize}>{children}</ThemeProvider>;
}
